import { tr } from "../i18n/LanguageContext";
import React from "react";
import { apiErrorMessage } from "../lib/apiError";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "../i18n/router";
import { User, Shield, LogOut, Mail, Loader2, BookOpen, Save, Clock, CheckCircle, Users, Camera, X } from "lucide-react";
import { practiceTypes } from "../data/mock";
import ImageCropModal from "../components/ImageCropModal";
import { readFileAsDataURL } from "../utils/cropImage";

const roleLabelMap = {
  admin: "Администратор",
  organizer: "Организатор",
  participant: "Участник",
};

const roleBgMap = {
  admin: "#dbeafe",
  organizer: "var(--eunoia-accent-whisper)",
  participant: "#f3f4f6",
};

const roleColorMap = {
  admin: "#1d4ed8",
  organizer: "var(--eunoia-accent)",
  participant: "#6b7280",
};

const ProfilePage = () => {
  const { user, loading: authLoading, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = React.useState({
    displayName: "", city: "", bio: "", practices: [],
    showInAttendees: false, showCity: false, avatarUrl: null,
  });
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [applicationStatus, setApplicationStatus] = React.useState(null);
  const [avatarUploading, setAvatarUploading] = React.useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = React.useState(null);  // data URL fed to crop modal
  // Session-scoped crop memory for the avatar (same pattern as AddEventPage).
  const lastAvatarCropRef = React.useRef({ imageSrc: null, crop: { x: 0, y: 0 }, zoom: 1 });
  const avatarInputRef = React.useRef(null);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!authLoading && !user) navigate("/login?redirect=/profile");
  }, [user, authLoading, navigate]);

  // Fetch organizer application status
  React.useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/organizers/application/status`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.hasApplication) setApplicationStatus(data);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [user, BACKEND_URL]);

  // Initialize form
  React.useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || "", city: user.city || "",
        bio: user.bio || "", practices: user.practices || [],
        showInAttendees: user.showInAttendees || false, showCity: user.showCity || false,
        avatarUrl: user.avatarUrl || null,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePracticeToggle = (practice) => {
    setFormData(prev => ({
      ...prev,
      practices: prev.practices.includes(practice)
        ? prev.practices.filter(p => p !== practice)
        : [...prev.practices, practice]
    }));
  };

  // Profile avatar upload. Mirrors the event-image upload pattern in
  // AddEventPage: validate format + size, preview, POST to /api/upload/image,
  // then persist the URL as user.avatarUrl on next Save. Removing an existing
  // photo sets avatarUrl:null and Save clears it — the initials fallback
  // then renders again everywhere the avatar appears.
  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      setMessage(tr("Неподдерживаемый формат. Разрешены: jpg, jpeg, png, webp"));
      setIsSuccess(false);
      setTimeout(() => setMessage(null), 3000);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage(tr("Файл слишком большой. Максимум: 5 МБ"));
      setIsSuccess(false);
      setTimeout(() => setMessage(null), 3000);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
      return;
    }

    // Feed the raw file into the crop modal — nothing is uploaded until the
    // user confirms the crop rectangle.
    try {
      const dataUrl = await readFileAsDataURL(file);
      setAvatarCropSrc(dataUrl);
    } catch {
      setMessage(tr("Не удалось прочитать файл"));
      setIsSuccess(false);
      setTimeout(() => setMessage(null), 3000);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleAvatarCropCancel = () => {
    setAvatarCropSrc(null);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };

  const handleAvatarCropConfirm = async (croppedBlob) => {
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([croppedBlob], "avatar.jpg", { type: croppedBlob.type || "image/jpeg" }));
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err, tr("Ошибка загрузки")));
      }
      const data = await res.json();
      const fullUrl = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      const result = await updateProfile({ avatarUrl: fullUrl });
      if (result.success) {
        setFormData(prev => ({ ...prev, avatarUrl: fullUrl }));
        setMessage(tr("Фото профиля обновлено"));
        setIsSuccess(true);
      } else {
        setMessage(result.message || tr("Не удалось сохранить фото"));
        setIsSuccess(false);
      }
    } catch (err) {
      setMessage(err.message || tr("Ошибка загрузки фото"));
      setIsSuccess(false);
    } finally {
      setAvatarUploading(false);
      setAvatarCropSrc(null);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarUploading(true);
    try {
      const result = await updateProfile({ avatarUrl: null });
      if (result.success) {
        setFormData(prev => ({ ...prev, avatarUrl: null }));
        setMessage(tr("Фото профиля удалено"));
        setIsSuccess(true);
      } else {
        setMessage(result.message || tr("Не удалось удалить фото"));
        setIsSuccess(false);
      }
    } finally {
      setAvatarUploading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const result = await updateProfile(formData);
      setMessage(result.success ? tr("Профиль успешно обновлен") : (result.message || tr("Ошибка")));
      setIsSuccess(result.success);
    } catch {
      setMessage(tr("Произошла ошибка"));
      setIsSuccess(false);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleLogout = async () => { await logout(); };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{color: 'var(--eunoia-accent)'}} />
      </div>
    );
  }

  const userRole = user.role || "participant";

  return (
    <div className="min-h-screen eunoia-space-lg pb-16" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">

        {/* SESSION INFO CARD — always visible at the top */}
        <div data-testid="session-info-card"
          className="eunoia-surface p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                 style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
              {user.avatarUrl
                ? <img src={user.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
                : <User className="h-6 w-6" style={{color: 'var(--eunoia-accent)'}} />}
            </div>
            <div className="min-w-0">
              <p data-testid="session-display-name" className="text-base font-semibold truncate" style={{color: 'var(--eunoia-deep)'}}>
                {user.displayName || tr("Пользователь")}
              </p>
              <p data-testid="session-email" className="text-sm truncate flex items-center gap-1.5" style={{color: 'var(--eunoia-soft)'}}>
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                {user.email}
              </p>
              <span data-testid="session-role-badge"
                className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: roleBgMap[userRole], color: roleColorMap[userRole] }}>
                <Shield className="h-3 w-3" />
                {tr(roleLabelMap[userRole])}
              </span>
            </div>
          </div>
          <button data-testid="session-logout-button" onClick={handleLogout}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors whitespace-nowrap">
            <LogOut className="h-4 w-4" /> {tr("Выйти")}
          </button>
        </div>

        {/* HEADER */}
        <div className="text-center mb-8">
          <h1 className="eunoia-title-large mb-2">{user.displayName || tr("Мой профиль")}</h1>
        </div>

        {/* Admin Link */}
        {userRole === 'admin' && (
          <div className="eunoia-surface-soft p-4 mb-6">
            <button onClick={() => navigate('/admin')} className="flex items-center justify-center gap-2 w-full eunoia-btn-secondary">
              <Shield className="h-5 w-5" /> {tr("Панель администратора")}
            </button>
          </div>
        )}

        {/* Organizer Application Status */}
        {applicationStatus && (
          <div className="eunoia-surface p-6 mb-6" data-testid="application-status-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{backgroundColor: applicationStatus.status === 'approved' ? '#dcfce7' : applicationStatus.status === 'rejected' ? '#fee2e2' : 'var(--eunoia-accent-whisper)'}}>
                {applicationStatus.status === 'pending'  && <Clock className="h-6 w-6" style={{color: 'var(--eunoia-accent)'}} />}
                {applicationStatus.status === 'approved' && <CheckCircle className="h-6 w-6" style={{color: '#16a34a'}} />}
                {applicationStatus.status === 'rejected' && <Users className="h-6 w-6" style={{color: '#dc2626'}} />}
              </div>
              <div className="flex-1">
                <p data-testid="application-status-text" className="eunoia-body font-medium" style={{color: applicationStatus.status === 'approved' ? '#16a34a' : applicationStatus.status === 'rejected' ? '#dc2626' : 'var(--eunoia-accent)'}}>
                  {applicationStatus.status === 'pending'  && tr("Ваша заявка на организатора на рассмотрении")}
                  {applicationStatus.status === 'approved' && tr("Ваша заявка одобрена")}
                  {applicationStatus.status === 'rejected' && tr("Ваша заявка отклонена")}
                </p>
              </div>
              {applicationStatus.status === 'approved' && (
                <button type="button" onClick={() => navigate('/add-event')} className="eunoia-btn-primary text-sm px-4 py-2">{tr("Создать событие")}</button>
              )}
            </div>
          </div>
        )}

        {/* Become Organizer CTA — only if no application exists */}
        {!applicationStatus && userRole !== 'organizer' && userRole !== 'admin' && (
          <div className="eunoia-surface-soft p-6 mb-6" data-testid="apply-organizer-cta">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
                  <Users className="h-6 w-6" style={{color: 'var(--eunoia-accent)'}} />
                </div>
                <div>
                  <h3 className="eunoia-title-small mb-1">{tr("Стать организатором")}</h3>
                  <p className="eunoia-caption">{tr("Создавайте события и объединяйте сообщество")}</p>
                </div>
              </div>
              <button type="button" data-testid="apply-organizer-button" onClick={() => navigate('/organizers/apply')} className="eunoia-btn-secondary text-sm">{tr("Подать заявку организатора")}</button>
            </div>
          </div>
        )}

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {message && (
            <div className={`p-4 rounded-xl text-center ${isSuccess ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message}</div>
          )}

          {/* Avatar / profile photo */}
          <div className="eunoia-surface p-6" data-testid="avatar-card">
            <h2 className="eunoia-title-small mb-6 flex items-center gap-2">
              <Camera className="h-5 w-5" style={{color: 'var(--eunoia-accent)'}} /> {tr("Фото профиля")}
            </h2>
            <div className="flex items-center gap-6 flex-wrap">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: 'var(--eunoia-accent-whisper)', border: '3px solid rgba(200,168,130,0.2)' }}
                data-testid="avatar-preview"
              >
                {formData.avatarUrl ? (
                  <img src={formData.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-medium" style={{ color: 'var(--eunoia-accent)' }}>
                    {(formData.displayName || user.email || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2 min-w-0">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={avatarUploading}
                    className="eunoia-btn-secondary text-sm inline-flex items-center gap-2"
                    data-testid="avatar-upload-button"
                  >
                    {avatarUploading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> {tr("Загрузка…")}</>
                    ) : (
                      <><Camera className="h-4 w-4" /> {formData.avatarUrl ? tr("Заменить фото") : tr("Загрузить фото")}</>
                    )}
                  </button>
                  {formData.avatarUrl && (
                    <button
                      type="button"
                      onClick={handleAvatarRemove}
                      disabled={avatarUploading}
                      className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-full text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
                      data-testid="avatar-remove-button"
                    >
                      <X className="h-4 w-4" /> {tr("Удалить")}
                    </button>
                  )}
                </div>
                <p className="text-xs" style={{color: 'var(--eunoia-soft)'}}>
                  {tr("JPG, PNG или WEBP до 5 МБ. Если фото не загружено, показываются ваши инициалы.")}
                </p>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarSelect}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
              </div>
            </div>
          </div>

          <div className="eunoia-surface p-6">
            <h2 className="eunoia-title-small mb-6 flex items-center gap-2">
              <User className="h-5 w-5" style={{color: 'var(--eunoia-accent)'}} /> {tr("Основная информация")}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="displayName" className="block eunoia-body mb-2">{tr("Отображаемое имя")}</label>
                <input id="displayName" name="displayName" type="text" value={formData.displayName} onChange={handleChange} className="eunoia-input" placeholder={tr("Как вас называть?")} />
              </div>
              <div>
                <label htmlFor="city" className="block eunoia-body mb-2">{tr("Город")}</label>
                <input id="city" name="city" type="text" value={formData.city} onChange={handleChange} className="eunoia-input" placeholder={tr("Ваш город")} />
              </div>
              <div>
                <label htmlFor="bio" className="block eunoia-body mb-2">{tr("О себе")}</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  maxLength={1000}
                  className="eunoia-input min-h-[100px] resize-y"
                  placeholder={tr("Расскажите немного о себе...")}
                  data-testid="bio-textarea"
                />
                <div className="flex justify-end mt-1">
                  <span
                    className="text-xs"
                    style={{ color: (formData.bio?.length || 0) >= 900 ? "#92400E" : "var(--eunoia-soft)" }}
                    data-testid="bio-counter"
                  >
                    {formData.bio?.length || 0} / 1000
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="eunoia-surface p-6">
            <h2 className="eunoia-title-small mb-6 flex items-center gap-2">
              <BookOpen className="h-5 w-5" style={{color: 'var(--eunoia-accent)'}} /> {tr("Мои практики")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {practiceTypes.map(p => (
                <button key={p.value} type="button" onClick={() => handlePracticeToggle(p.value)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${formData.practices.includes(p.value) ? 'text-white' : 'bg-white/50 hover:bg-white/80'}`}
                  style={{ backgroundColor: formData.practices.includes(p.value) ? 'var(--eunoia-accent)' : undefined }}>
                  {tr(p.label)}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} className="eunoia-btn-primary w-full flex items-center justify-center gap-2">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5" /> {tr("Сохранить изменения")}</>}
          </button>
        </form>

        {/* Bottom Logout */}
        <div className="mt-8 pt-8 border-t" style={{borderColor: 'var(--eunoia-whisper)'}}>
          <button data-testid="profile-logout-button" onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors">
            <LogOut className="h-5 w-5" /> {tr("Выйти из аккаунта")}
          </button>
        </div>
      </div>
      {avatarCropSrc && (
        <ImageCropModal
          imageSrc={avatarCropSrc}
          aspect={1}
          cropShape="round"
          title={tr("Обрежьте фото профиля")}
          onCancel={handleAvatarCropCancel}
          onConfirm={handleAvatarCropConfirm}
          confirming={avatarUploading}
          initialCrop={lastAvatarCropRef.current.imageSrc === avatarCropSrc ? lastAvatarCropRef.current.crop : { x: 0, y: 0 }}
          initialZoom={lastAvatarCropRef.current.imageSrc === avatarCropSrc ? lastAvatarCropRef.current.zoom : 1}
          onStateChange={({ crop, zoom }) => { lastAvatarCropRef.current = { imageSrc: avatarCropSrc, crop, zoom }; }}
        />
      )}
    </div>
  );
};

export default ProfilePage;
